<template>
  <div class="navigator">
    <div class="navigator-heading">
      <div>
        <p class="eyebrow">{{ searchActive ? 'Search results' : 'Ceremony guide' }}</p>
        <h3>{{ searchActive ? 'Matching moments' : 'Chapters and events' }}</h3>
      </div>
      <button
        v-if="searchActive"
        type="button"
        class="text-button"
        @click="$emit('show-guide')"
      >
        Back to guide
      </button>
    </div>

    <div v-if="searchLoading" class="navigator-state" aria-live="polite">
      <span class="loading-dot"></span>
      Searching this ceremony…
    </div>

    <div v-else-if="searchActive" class="search-results">
      <p v-if="searchNotice" class="search-notice">
        {{ searchNotice }}
      </p>
      <p v-if="searchError" class="navigator-state">
        {{ searchError }}
      </p>
      <section v-for="group in searchResultGroups" :key="group.key" class="result-group">
        <p class="result-group-label">{{ group.label }}</p>
        <button
          v-for="item in group.items"
          :key="item.id"
          type="button"
          class="result-row"
          :class="{ active: item.id === selectedEventId }"
          @click="$emit('select-item', item)"
        >
          <span class="result-copy">
            <strong>{{ item.title }}</strong>
            <span>{{ item.description }}</span>
            <small v-if="item.ceremonyLabel">{{ item.ceremonyLabel }}</small>
          </span>
          <time>{{ formatDuration(item.timestamp) }}</time>
        </button>
        <button
          v-if="group.hiddenCount"
          type="button"
          class="show-more-results"
          @click="showAllVisualResults"
        >
          Show {{ group.hiddenCount }} more visual
          {{ group.hiddenCount === 1 ? 'match' : 'matches' }}
        </button>
      </section>
      <button
        v-if="!searchResults.length"
        type="button"
        class="guide-return"
        @click="$emit('show-guide')"
      >
        Browse the ceremony guide instead
      </button>
    </div>

    <div v-else-if="chapters.length" class="chapters">
      <details
        v-for="chapter in chapters"
        :key="chapter.id"
        :open="expandedChapterIds.includes(chapter.id)"
        class="chapter"
      >
        <summary
          :class="{ active: chapter.id === selectedEventId }"
          @click.prevent="toggleChapter(chapter)"
        >
          <span class="chapter-copy">
            <strong>{{ chapter.title }}</strong>
            <span>
              {{ formatDuration(chapter.timestamp) }}
              <template v-if="chapter.events.length">
                · {{ chapter.events.length }} event{{ chapter.events.length === 1 ? '' : 's' }}
              </template>
            </span>
          </span>
          <span class="chevron" aria-hidden="true">⌄</span>
        </summary>

        <div v-if="chapter.events.length" class="chapter-events">
          <button
            v-for="event in chapter.events"
            :key="event.id"
            type="button"
            class="event-row"
            :class="{ active: event.id === selectedEventId }"
            @click="$emit('select-item', event)"
          >
            <span>{{ event.title }}</span>
            <time>{{ formatDuration(event.timestamp) }}</time>
          </button>
        </div>
        <p v-else class="chapter-empty">Play from the start of this chapter.</p>
      </details>
    </div>

    <div v-else class="navigator-state">
      <strong>No ceremony guide yet</strong>
      <span>Search for a specific moment, or ask an administrator to build chapters.</span>
    </div>
  </div>
</template>

<script>
const INITIAL_VISUAL_RESULT_COUNT = 6

export default {
  name: 'CeremonyChapterNavigator',
  props: {
    chapters: {
      type: Array,
      default: () => [],
    },
    searchResults: {
      type: Array,
      default: () => [],
    },
    searchActive: {
      type: Boolean,
      default: false,
    },
    searchLoading: {
      type: Boolean,
      default: false,
    },
    searchError: {
      type: String,
      default: '',
    },
    searchNotice: {
      type: String,
      default: '',
    },
    selectedEventId: {
      type: String,
      default: '',
    },
    formatDuration: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      expandedChapterIds: [],
      visualResultsExpanded: false,
    }
  },
  watch: {
    chapters: {
      immediate: true,
      handler(chapters) {
        if (!this.expandedChapterIds.length && chapters[0]) {
          this.expandedChapterIds = [chapters[0].id]
        }
      },
    },
    selectedEventId: {
      immediate: true,
      handler(eventId) {
        const chapter = this.chapters.find(item => (
          item.id === eventId
          || item.events.some(event => event.id === eventId)
        ))
        if (chapter && !this.expandedChapterIds.includes(chapter.id)) {
          this.expandedChapterIds = [...this.expandedChapterIds, chapter.id]
        }
      },
    },
    searchResults() {
      this.visualResultsExpanded = false
    },
  },
  computed: {
    searchResultGroups() {
      const groups = [
        { key: 'exact_spoken', label: 'Exact spoken matches' },
        { key: 'fuzzy_spoken', label: 'Close spoken matches' },
        { key: 'visual', label: 'Visual matches' },
        { key: 'spoken', label: 'Spoken and ceremony structure' },
      ]
      return groups
        .map(group => {
          const items = this.searchResults.filter(item => (
            (item.evidenceGroup || (item.type === 'frame' ? 'visual' : 'spoken')) === group.key
          ))
          const hiddenCount = group.key === 'visual' && !this.visualResultsExpanded
            ? Math.max(0, items.length - INITIAL_VISUAL_RESULT_COUNT)
            : 0
          return {
            ...group,
            items: hiddenCount ? items.slice(0, INITIAL_VISUAL_RESULT_COUNT) : items,
            hiddenCount,
          }
        })
        .filter(group => group.items.length)
    },
  },
  methods: {
    showAllVisualResults() {
      this.visualResultsExpanded = true
    },
    toggleChapter(chapter) {
      const expanded = this.expandedChapterIds.includes(chapter.id)
      this.expandedChapterIds = expanded
        ? this.expandedChapterIds.filter(item => item !== chapter.id)
        : [...this.expandedChapterIds, chapter.id]
      this.$emit('select-item', chapter)
    },
  },
}
</script>

<style scoped>
.navigator {
  height: 100%;
  min-height: 420px;
  padding: 20px;
  border: 1px solid #e4e8ef;
  border-radius: 16px;
  background: #fff;
}

.navigator-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.eyebrow {
  margin: 0 0 4px;
  color: #7c3aed;
  font-size: 0.7rem;
  font-weight: 750;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

h3 {
  margin: 0;
  color: #172033;
  font-size: 1rem;
  font-weight: 700;
}

.text-button,
.guide-return {
  border: 0;
  background: transparent;
  color: #6941c6;
  font-size: 0.78rem;
  font-weight: 700;
}

.chapters,
.search-results {
  display: grid;
  gap: 8px;
}

.result-group + .result-group {
  margin-top: 16px;
}

.result-group-label {
  margin: 0 0 8px;
  color: #657085;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.show-more-results {
  width: 100%;
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid #ddd6fe;
  border-radius: 10px;
  background: #f5f3ff;
  color: #6d28d9;
  font-weight: 600;
  text-align: center;
}

.show-more-results:hover,
.show-more-results:focus-visible {
  border-color: #8b5cf6;
  background: #ede9fe;
}

.search-notice {
  margin: 0 0 6px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f5f3ff;
  color: #5b21b6;
  font-size: 0.76rem;
  line-height: 1.4;
}

.result-copy small {
  color: #7c3aed;
  font-size: 0.68rem;
  font-weight: 650;
}

.chapter {
  overflow: hidden;
  border: 1px solid #e7eaf0;
  border-radius: 12px;
  background: #fbfcfe;
}

.chapter summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 13px;
  cursor: pointer;
  list-style: none;
}

.chapter summary::-webkit-details-marker {
  display: none;
}

.chapter summary.active,
.result-row.active,
.event-row.active {
  background: #f2edff;
  box-shadow: inset 3px 0 #7c3aed;
}

.chapter-copy,
.result-copy {
  display: grid;
  min-width: 0;
  gap: 3px;
  text-align: left;
}

.chapter-copy strong,
.result-copy strong {
  overflow-wrap: anywhere;
  color: #1d2939;
  font-size: 0.84rem;
  line-height: 1.35;
}

.chapter-copy span,
.result-copy span {
  color: #667085;
  font-size: 0.72rem;
  line-height: 1.35;
}

.chevron {
  flex: 0 0 auto;
  color: #7c8494;
  transition: transform 160ms ease;
}

.chapter[open] .chevron {
  transform: rotate(180deg);
}

.chapter-events {
  display: grid;
  gap: 2px;
  padding: 0 7px 7px 14px;
  border-top: 1px solid #edf0f4;
}

.event-row,
.result-row {
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  border: 0;
  border-radius: 8px;
  padding: 10px;
  background: transparent;
  color: #344054;
  text-align: left;
}

.event-row:hover,
.result-row:hover {
  background: #f5f3ff;
}

.event-row span {
  overflow-wrap: anywhere;
  font-size: 0.78rem;
  line-height: 1.4;
}

time {
  flex: 0 0 auto;
  color: #7c8494;
  font-size: 0.7rem;
  font-variant-numeric: tabular-nums;
}

.chapter-empty,
.navigator-state {
  margin: 0;
  color: #667085;
  font-size: 0.78rem;
  line-height: 1.5;
}

.chapter-empty {
  padding: 10px 13px 12px;
  border-top: 1px solid #edf0f4;
}

.navigator-state {
  display: grid;
  justify-items: start;
  gap: 6px;
  padding: 18px 4px;
}

.loading-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #7c3aed;
  animation: pulse 1s ease-in-out infinite alternate;
}

.guide-return {
  padding: 10px 0;
}

@keyframes pulse {
  to {
    opacity: 0.35;
  }
}

@media (prefers-reduced-motion: reduce) {
  .chevron,
  .loading-dot {
    animation: none;
    transition: none;
  }
}

@media (max-width: 899px) {
  .navigator {
    min-height: 0;
  }
}
</style>
